import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Users, Mail, Shield, RotateCcw, Archive, UserCheck, Eye, MoreHorizontal, Trash2, AlertTriangle, Loader2, XCircle } from 'lucide-react';
import { generateInitials, generateDisplayName } from '@/utils/avatarUtils';
import { HourlyRateDisplay } from '@/components/team/HourlyRateDisplay';

interface TeamMember {
  id: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  status: string | null;
  avatar_url: string | null;
}

interface TeamMemberCardProps {
  member: TeamMember;
  onResendInvitation?: (member: TeamMember) => void;
  resendingInvitation?: string | null;
  onArchiveMember?: (member: TeamMember) => void;
  archivingMember?: string | null;
  onReactivateMember?: (member: TeamMember) => void;
  reactivatingMember?: string | null;
  onDeleteMember?: (member: TeamMember) => void;
  deletingMember?: string | null;
  onCancelPendingInvitation?: (member: TeamMember) => void;
  cancellingPendingInvitation?: string | null;
  onViewProfile?: (member: TeamMember) => void;
  isArchived?: boolean;
  showPayRate?: boolean;
}

export function TeamMemberCard({
  member,
  onResendInvitation,
  resendingInvitation,
  onArchiveMember,
  archivingMember,
  onReactivateMember,
  reactivatingMember,
  onDeleteMember,
  deletingMember,
  onCancelPendingInvitation,
  cancellingPendingInvitation,
  onViewProfile,
  isArchived = false,
  showPayRate = true,
}: TeamMemberCardProps) {
  // State for controlling the delete dialog (separate from dropdown to avoid Radix issues)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // State for controlling the cancel pending invitation dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // Helper function to identify pending users
  const isPendingUser = (member: TeamMember): boolean => {
    return !member.first_name || !member.last_name;
  };
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return <Shield className="h-4 w-4 text-red-500" />;
      case 'MANAGER':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'STAFF':
        return <Mail className="h-4 w-4 text-green-500" />;
      default:
        return <Mail className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'MANAGER':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'STAFF':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Member Name */}
          <div className="flex items-center space-x-3">
            <Avatar className="h-12 w-12">
              <AvatarImage 
                src={member.avatar_url} 
                alt={generateDisplayName(member.first_name, member.last_name)}
              />
              <AvatarFallback>
                {generateInitials(member.first_name, member.last_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">
                {member.first_name && member.last_name 
                  ? `${member.first_name} ${member.last_name}`
                  : 'Pending'
                }
              </h3>
              <p className="text-sm text-muted-foreground">{member.email}</p>
            </div>
          </div>

          {/* Role */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Role:</span>
            <div className="flex items-center space-x-2">
              {getRoleIcon(member.role)}
              <Badge 
                className={`text-xs font-medium border ${getRoleBadgeColor(member.role)}`}
                variant="outline"
              >
                {member.role}
              </Badge>
            </div>
          </div>

          {/* Pay Rate - only show for admins */}
          {showPayRate && (
            <div className="space-y-2">
              <span className="text-sm font-medium">Pay Rate:</span>
              <HourlyRateDisplay profileId={member.id} />
            </div>
          )}

          {/* Joined Date and Actions */}
          <div className="pt-2 border-t">
            <div className="flex flex-col gap-2">
              <span className="text-sm text-muted-foreground">
                Joined: {new Date(member.created_at).toLocaleDateString()}
              </span>
              
              <div className="flex flex-wrap gap-2">
                {/* View Profile Button - only show for non-pending users */}
                {!isPendingUser(member) && onViewProfile && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onViewProfile(member)}
                    className="text-xs"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    View Profile
                  </Button>
                )}

                {/* Pending User Actions: Resend + Cancel */}
                {isPendingUser(member) && (
                  <>
                    {onResendInvitation && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onResendInvitation(member)}
                        disabled={resendingInvitation === member.id || cancellingPendingInvitation === member.id}
                        className="text-xs"
                      >
                        {resendingInvitation === member.id ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Resend
                          </>
                        )}
                      </Button>
                    )}
                    
                    {/* Cancel Invitation Dropdown + Dialog */}
                    {onCancelPendingInvitation && (
                      <>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0"
                              disabled={cancellingPendingInvitation === member.id}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => setShowCancelDialog(true)}
                              className="text-destructive focus:text-destructive"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancel Invitation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        
                        {/* Controlled AlertDialog for Cancel Invitation */}
                        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-destructive" />
                                Cancel Pending Invitation
                              </AlertDialogTitle>
                              <AlertDialogDescription className="space-y-2">
                                <p>
                                  Are you sure you want to cancel the invitation for <strong>{member.email}</strong>?
                                </p>
                                <p className="text-destructive font-medium">
                                  This will permanently remove this pending user. They will need to be invited again to join.
                                </p>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => {
                                  onCancelPendingInvitation(member);
                                  setShowCancelDialog(false);
                                }}
                                disabled={cancellingPendingInvitation === member.id}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                {cancellingPendingInvitation === member.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Cancelling...
                                  </>
                                ) : (
                                  'Cancel Invitation'
                                )}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </>
                )}

                {/* Archive Button - only show for active members */}
                {!isArchived && !isPendingUser(member) && onArchiveMember && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={archivingMember === member.id}
                        className="text-xs text-orange-600 hover:text-orange-700"
                      >
                        {archivingMember === member.id ? (
                          <>Loading...</>
                        ) : (
                          <>
                            <Archive className="h-3 w-3 mr-1" />
                            Archive
                          </>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Archive Team Member</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to archive {member.first_name && member.last_name ? `${member.first_name} ${member.last_name}` : member.email}? 
                          They will be removed from active views but their historical data will be preserved.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onArchiveMember(member)}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          Archive
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}

                {/* Reactivate Button - only show for archived members */}
                {isArchived && onReactivateMember && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reactivatingMember === member.id}
                        className="text-xs text-green-600 hover:text-green-700"
                      >
                        {reactivatingMember === member.id ? (
                          <>Loading...</>
                        ) : (
                          <>
                            <UserCheck className="h-3 w-3 mr-1" />
                            Reactivate
                          </>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reactivate Team Member</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to reactivate {member.first_name && member.last_name ? `${member.first_name} ${member.last_name}` : member.email}? 
                          They will be restored to active status and appear in team views again.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onReactivateMember(member)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Reactivate
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}

                {/* Delete Permanently - only show for archived members */}
                {isArchived && onDeleteMember && (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => setShowDeleteDialog(true)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    {/* Controlled AlertDialog - separate from dropdown to avoid Radix issues */}
                    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-destructive flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5" />
                            Permanently Delete Team Member
                          </AlertDialogTitle>
                          <AlertDialogDescription asChild>
                            <div className="space-y-3">
                              <p>
                                You are about to <strong>permanently delete</strong>{' '}
                                <span className="font-medium">
                                  {member.first_name && member.last_name 
                                    ? `${member.first_name} ${member.last_name}` 
                                    : member.email}
                                </span>.
                              </p>
                              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                                <p className="text-destructive font-medium text-sm mb-2">
                                  This action is IRREVERSIBLE and will:
                                </p>
                                <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                                  <li>Remove their account and login access permanently</li>
                                  <li>Delete their leave balances and policy assignments</li>
                                  <li>Anonymize their historical time entries and payslips</li>
                                  <li>Remove them from any approver chains</li>
                                </ul>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                If this person is re-added later, they will be treated as a completely new user with no prior history.
                              </p>
                            </div>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              onDeleteMember(member);
                              setShowDeleteDialog(false);
                            }}
                            disabled={deletingMember === member.id}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            {deletingMember === member.id ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Deleting...
                              </>
                            ) : (
                              'Delete Permanently'
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
